import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { supabase } from "@/integrations/supabase/client";
import { Toaster } from "@/components/ui/sonner";
import { BundleUpdateFallback } from "@/components/BundleUpdateFallback";
import { registerServiceWorker } from "@/lib/register-sw";
import { ForceUpdateGate } from "@/components/ForceUpdateGate";
import { AppBootGate } from "@/components/AppBootGate";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { installGlobalErrorHandlers, logAppError } from "@/lib/error-logger";
import { installBreadcrumbs } from "@/lib/breadcrumbs";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-primary">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
    logAppError({
      message: error?.message ?? String(error),
      stack: error?.stack,
      severity: "error",
      category: "frontend",
      meta: { boundary: "tanstack_root_error_component" },
    });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong. Try again or head home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            Try again
          </button>
          <a href="/" className="rounded-md border border-input px-4 py-2 text-sm font-medium">
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, maximum-scale=1" },
      { name: "theme-color", content: "#4c1d95" },
      { title: "Brothers Club — Play. Win. Repeat." },
      { name: "description", content: "Brothers Club — join daily games, add money to your wallet, refer friends and win big." },
      { property: "og:title", content: "Brothers Club — Play. Win. Repeat." },
      { property: "og:description", content: "Brothers Club — join daily games, add money to your wallet, refer friends and win big." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Brothers Club — Play. Win. Repeat." },
      { name: "twitter:description", content: "Brothers Club — join daily games, add money to your wallet, refer friends and win big." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/ddb67a82-0ed1-4355-899c-61165a232a2b/id-preview-23a3ce31--a742e956-3f36-44a7-9eab-7291e8004484.lovable.app-1783321512064.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/ddb67a82-0ed1-4355-899c-61165a232a2b/id-preview-23a3ce31--a742e956-3f36-44a7-9eab-7291e8004484.lovable.app-1783321512064.png" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      // Google Fonts — `display=swap` already renders text with a fallback
      // font first, so this is non-blocking without needing an onLoad hack
      // (which broke React hydration and printed a runtime warning).
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" },
      { rel: "icon", href: "/icon-192.png", type: "image/png" },
      { rel: "apple-touch-icon", href: "/icon-192.png" },
      { rel: "manifest", href: "/manifest.webmanifest" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              name: "Brothers Club",
              url: "https://brothersclub.co.in",
              logo: "https://brothersclub.co.in/icon-512.png",
            },
            {
              "@type": "WebSite",
              name: "Brothers Club",
              url: "https://brothersclub.co.in",
            },
          ],
        }),
      },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

// Painted by the SERVER, so the Android WebView has visible branded pixels the
// instant the HTML arrives — this is what removes the white screen on a cold
// launch (the app shell itself is client-rendered). AppBootGate removes it once
// React owns the screen; the inline watchdog turns it into a retry screen if
// the JS bundles never arrive (stale cache / dead network).
const BOOT_SPLASH_CSS = `
html.bc-booting, html.bc-booting body { background:#4c1d95; }
#bc-boot-splash{position:fixed;inset:0;z-index:2147483000;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;background:#4c1d95;color:#fff;font-family:Inter,system-ui,-apple-system,sans-serif;text-align:center;padding:24px}
#bc-boot-splash .bc-mark{width:80px;height:80px;border-radius:18px;background:rgba(255,255,255,.12);display:flex;align-items:center;justify-content:center;font-weight:900;font-size:26px;letter-spacing:-.02em}
#bc-boot-splash .bc-name{font-weight:900;font-size:18px}
#bc-boot-splash .bc-msg{font-size:13px;opacity:.8;max-width:20rem}
#bc-boot-splash .bc-spin{width:22px;height:22px;border-radius:50%;border:2px solid rgba(255,255,255,.35);border-top-color:#fff;animation:bcspin .8s linear infinite}
#bc-boot-splash .bc-retry{margin-top:6px;display:none;background:#fff;color:#4c1d95;font-weight:700;font-size:13px;border:0;border-radius:12px;padding:10px 20px}
@keyframes bcspin{to{transform:rotate(360deg)}}
`;

// Fail-safe that does not depend on the React bundle loading at all.
const BOOT_WATCHDOG_JS = `
(function(){
  try{
    document.documentElement.classList.add('bc-booting');
    var slow=setTimeout(function(){
      var m=document.getElementById('bc-boot-msg');
      if(m&&!window.__BC_BOOTED)m.textContent='Taking longer than expected\\u2026';
    },3500);
    var hard=setTimeout(function(){
      if(window.__BC_BOOTED)return;
      var m=document.getElementById('bc-boot-msg');
      var b=document.getElementById('bc-boot-retry');
      var s=document.querySelector('#bc-boot-splash .bc-spin');
      if(s)s.style.display='none';
      if(m)m.textContent=(navigator.onLine===false)
        ? 'No internet connection. Connect and tap Retry.'
        : 'Still loading. Tap Retry to restart the app.';
      if(b)b.style.display='inline-block';
      console.warn('[boot] STARTUP_TIMEOUT (pre-hydration watchdog)');
    },10000);
    window.addEventListener('online',function(){ if(!window.__BC_BOOTED) location.reload(); });
    document.addEventListener('DOMContentLoaded',function(){
      var b=document.getElementById('bc-boot-retry');
      if(b)b.addEventListener('click',function(){ location.reload(); });
    });
    window.__BC_BOOT_CLEAR=function(){clearTimeout(slow);clearTimeout(hard);};
  }catch(e){}
})();
`;

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" className="bc-booting">
      <head>
        <HeadContent />
        <style dangerouslySetInnerHTML={{ __html: BOOT_SPLASH_CSS }} />
        <script dangerouslySetInnerHTML={{ __html: BOOT_WATCHDOG_JS }} />
      </head>
      <body>
        <div id="bc-boot-splash">
          <div className="bc-mark">BC</div>
          <div className="bc-name">Brothers Club</div>
          <div className="bc-spin" />
          <div className="bc-msg" id="bc-boot-msg">Loading Brothers Club…</div>
          <button type="button" className="bc-retry" id="bc-boot-retry">Retry</button>
        </div>
        {children}
        <Scripts />
      </body>
    </html>
  );
}


// Query keys we want to survive across cold starts so the home page renders
// instantly from cache on 2nd+ launch. Keep this list tight — never persist
// wallet balance, user identity, or anything sensitive/frequently-changing
// in a way that would leak stale money numbers.
const PERSIST_KEYS = new Set([
  "app_settings",
  "games_active",
  "banners",
  "notices_active",
]);

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    // Hydrate persisted queries from localStorage synchronously so first paint
    // of the home page can use cached games/banners/notices/settings.
    void (async () => {
      try {
        const [{ persistQueryClient }, { createSyncStoragePersister }] = await Promise.all([
          import("@tanstack/react-query-persist-client"),
          import("@tanstack/query-sync-storage-persister"),
        ]);
        const persister = createSyncStoragePersister({
          storage: window.localStorage,
          key: "bc_rq_cache_v1",
          throttleTime: 1000,
        });
        persistQueryClient({
          queryClient,
          persister,
          maxAge: 24 * 60 * 60 * 1000, // 24h
          buster: "v1",
          dehydrateOptions: {
            shouldDehydrateQuery: (q) => {
              const k0 = Array.isArray(q.queryKey) ? String(q.queryKey[0]) : "";
              return PERSIST_KEYS.has(k0) && q.state.status === "success";
            },
          },
        });
      } catch (e) {
        // Persistence is a nice-to-have; never break the app if it fails.
        console.warn("query persist init failed", e);
      }
    })();

    registerServiceWorker();
    installGlobalErrorHandlers();
    installBreadcrumbs();
    void import("@/lib/native-shell").then((m) => m.installNativeShell(router)).catch(() => {});

    // Defer push init off the critical path. It must not compete with the
    // first paint or the initial home-page data fetches.
    const schedule = (cb: () => void) => {
      const w = window as any;
      if (typeof w.requestIdleCallback === "function") {
        w.requestIdleCallback(cb, { timeout: 3000 });
      } else {
        setTimeout(cb, 2500);
      }
    };
    schedule(() => {
      void import("@/lib/push").then((m) => { m.setPushRouter(router); m.registerPushNotifications(); }).catch(() => {});
    });

    let lastUserId: string | null = null;
    void supabase.auth.getSession().then(({ data }) => { lastUserId = data.session?.user?.id ?? null; });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      router.invalidate();
      if (event === "SIGNED_OUT") {
        const uid = lastUserId;
        if (uid) void import("@/lib/push").then((m) => m.unregisterPushNotificationsForCurrentUser(uid)).catch(() => {});
        lastUserId = null;
      } else {
        lastUserId = session?.user?.id ?? null;
        queryClient.invalidateQueries();
        schedule(() => {
          void import("@/lib/push").then((m) => { m.setPushRouter(router); m.registerPushNotifications(); }).catch(() => {});
        });
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [router, queryClient]);


  return (
    <QueryClientProvider client={queryClient}>
      <AppErrorBoundary scope="customer">
        <AppBootGate>
          <ForceUpdateGate>
            <Outlet />
          </ForceUpdateGate>
        </AppBootGate>
      </AppErrorBoundary>
      <Toaster position="top-center" richColors />
      <BundleUpdateFallback />
    </QueryClientProvider>
  );
}

